import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { AddUserIcon } from "@/components/Icons/AddUserIcon";
import { AptihealthIcon } from "@/components/Icons/AptihealthIcon";
import { BellIcon } from "@/components/Icons/BellIcon";
import { CameraIcon } from "@/components/Icons/CameraIcon";
import { CardIcon } from "@/components/Icons/CardIcon";
import { ChevronDownIcon } from "@/components/Icons/ChevronDownIcon";
import { DocumentIcon } from "@/components/Icons/DocumentIcon";
import { EnvelopeIcon } from "@/components/Icons/EnvelopeIcon";
import { ExitIcon } from "@/components/Icons/ExitIcon";
import { LockIcon } from "@/components/Icons/LockIcon";
import { ShieldIcon } from "@/components/Icons/ShieldIcon";
import { TourIcon } from "@/components/Icons/TourIcon";
import { UserIcon } from "@/components/Icons/UserIcon";
import { ICON_SIZES, ICON_TYPES } from "@/constants/icons";
import { getPatientAge } from "@/containers/Dashboard/Providers/schema/newPatientValidation";
import { cn } from "@/lib/helpers/cn";
import { logOut } from "@/redux/actions/auth";
import { selectIsACPEligible } from "@/redux/selectors/patientSelectors";
import { shouldShowVulnerablePages } from "@/utils/authUtils";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { usePopUpVisibility } from "@/utils/hooks";
import { isAdult } from "@/utils/profileUtilsTyped";

import { MenuItem } from "./MenuItem";
import { MenuSection } from "./MenuSection";
import "./profile-menu-styles.scss";

export function PatientMenu(props) {
  const [popupWasOpened, setPopupWasOpened] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const isACPEligible = useSelector(selectIsACPEligible);
  const { hidePopup, isPopUpVisible, ref: popUpRef, togglePopup } = usePopUpVisibility(false);

  const isPatientAdult = isAdult(props.profile?.dob);
  const isMobile = window.innerWidth <= 640;
  const firstName = props?.profile?.preferred_name || props?.profile?.first_name;
  let name = props.profile ? firstName + " " + props.profile.last_name : "";

  const displayAddChild = () => {
    return 18 < getPatientAge(props.profile.dob);
  };

  const dismissMenu = () => hidePopup();

  const toggleProfileMenu = () => {
    if (isMobile) {
      history.push(props.baseRoute + "/settings");

      return;
    }

    togglePopup();
  };

  const handleProfile = () => {
    dismissMenu();
    history.push(props.baseRoute + "/profile");
  };

  const handlePassword = () => {
    dismissMenu();
    history.push(props.baseRoute + "/change-password");
  };

  const handleAddChild = () => {
    dismissMenu();
    history.push(props.baseRoute + "/teens-and-kids/program-details");
  };

  const handleNotification = () => {
    dismissMenu();
    history.push(props.baseRoute + "/patient-notifications");
  };

  const handleTutorials = () => {
    dismissMenu();
    history.push(props.baseRoute + "/tutorial");
  };

  const handleSystemCheck = () => {
    dismissMenu();
    history.push(props.baseRoute + "/system-check");
  };

  const handleInsurance = () => {
    dismissMenu();
    history.push(props.baseRoute + "/patient-insurance");
  };

  const handlePayment = () => {
    dismissMenu();
    history.push(props.baseRoute + "/patient-payment");
  };

  const handleLogout = () => {
    dismissMenu();
    dispatch(logOut({ history }));
  };

  const handleConsents = () => {
    dismissMenu();
    history.push(props.baseRoute + "/consents");
  };

  useEffect(() => {
    if (!popupWasOpened && isPopUpVisible) {
      setPopupWasOpened(true);
    }
  }, [isPopUpVisible]);

  const profileDropdown = (
    <div style={{ display: popupWasOpened ? "block" : "none" }}>
      <div
        className={cn(
          "Toolbar__ProfileMenu-dropdown tw:flex tw:flex-col tw:gap-4 tw:p-5",
          isPopUpVisible ? "menuOpen" : "menuClose",
          !isPopUpVisible && "disabled-dropdown",
        )}
      >
        <MenuSection title="ACCOUNT">
          <MenuItem icon={UserIcon} onClick={handleProfile} text="Profile" />

          <MenuItem
            automationDataAttr={getAutomationDataAttr("ProfileMenuChangePassword")}
            icon={LockIcon}
            onClick={handlePassword}
            text="Password"
          />

          <MenuItem
            className="patient-dropdown-text" // CYPRESS TEST: MemberHeader.js:14 - DO NOT REMOVE
            icon={ShieldIcon}
            onClick={handleInsurance}
            text="Insurance"
            {...getAutomationDataAttr("ProfileMenuInsuranceItem")}
          />

          {shouldShowVulnerablePages(props.profile.status) && (
            <MenuItem icon={CardIcon} onClick={handlePayment} text="Payment" />
          )}

          {displayAddChild() && (
            <MenuItem icon={AddUserIcon} onClick={handleAddChild} text="Add Child" />
          )}
          <MenuItem icon={BellIcon} onClick={handleNotification} text="Notifications" />
        </MenuSection>

        <MenuSection title="SUPPORT">
          <MenuItem
            href="https://www.aptihealth.com/about/"
            icon={AptihealthIcon}
            rel="noreferrer"
            target="_blank"
            text="About aptihealth"
          />

          <MenuItem
            href="mailto:support@aptihealth.com?subject=Web Support Request"
            icon={EnvelopeIcon}
            target="_top"
            text="Member Support"
          />

          <MenuItem icon={TourIcon} onClick={handleTutorials} text="Tutorials" />
          <MenuItem icon={CameraIcon} onClick={handleSystemCheck} text="System & Video Check" />
        </MenuSection>

        <MenuSection title="LEGAL">
          {isACPEligible && isPatientAdult && (
            <MenuItem
              automationDataAttr={getAutomationDataAttr("profile-menu-consents")}
              icon={DocumentIcon}
              onClick={handleConsents}
              text="Consents"
            />
          )}

          <MenuItem
            href="https://www.aptihealth.com/hipaaprivacy"
            icon={DocumentIcon}
            rel="noreferrer"
            target="_blank"
            text="HIPAA Notice"
          />
        </MenuSection>

        <MenuItem icon={ExitIcon} onClick={handleLogout} text="Log Out" />
      </div>
    </div>
  );

  const profileMenuItem = (
    <div
      className="Toolbar__ProfileMenu cursor d-flex align-items-center"
      onClick={toggleProfileMenu}
    >
      <div
        className="Toolbar__ProfileMenu-name-wpr d-lg-flex flex-column d-md-none d-sm-none d-none align-content-end"
        style={{ marginTop: 0 }}
      >
        <span
          className={cn(
            "fs-16 txt-ellipsis NavUserNameSpanPatient", // CYPRESS TEST: MemberHeader.js:38-39 - DO NOT REMOVE
            "tw:hover:text-text-teal-secondary",
          )}
          style={{ whiteSpace: "nowrap" }}
        >
          {name}
        </span>
      </div>

      <div
        className="Toolbar__ProfileMenu-caret ml-2 d-lg-block d-md-none d-sm-none d-none" // CYPRESS TEST: MemberHeader.js:6 - DO NOT REMOVE
        {...getAutomationDataAttr("ProfileMenu")}
      >
        <ChevronDownIcon className="Toolbar__ChevronDown" iconSize={ICON_SIZES.SMALL} />
      </div>

      <div
        className="Toolbar__ProfileMenu-name-wpr d-block d-lg-none d-flex flex-column align-items-center"
        style={{ fill: "#FFFFFF", marginTop: 0 }}
      >
        <UserIcon iconSize={ICON_SIZES.SMALL} iconType={ICON_TYPES.OUTLINE} />
        <span className="ToolbarUserName fs-16 txt-ellipsis" style={{ whiteSpace: "nowrap" }}>
          {/* CYPRESS TEST: Used in mobile view - DO NOT REMOVE */}
          {name}
        </span>
      </div>
    </div>
  );

  return (
    <div className="tw:relative tw:flex tw:items-center" ref={popUpRef}>
      {props.profile ? profileMenuItem : <span className="Toolbar__ProfileMenu-placeholder" />}
      {profileDropdown}
    </div>
  );
}
